import {DataCollaborator, OwnerInfo} from 'auth/PermissionConstants';
import {GatewayType} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {
  AdapterField,
  InstrumentAdapter,
  InstrumentAdapterConfiguration,
  InstrumentServiceDetails,
} from './ConnectionConstants';

export function connectionURL(connectionId: string): string {
  return `/connections/${connectionId}`;
}

export function createConnection(args: {
  schemaId: string;
  name: string;
  tags: TagUpdateObject;
  gatewayId: string;
  dataCatalogFolderId: string | null;
  equipmentIds: Array<string>;
}): Promise<{id: string}> {
  return net.postJSONPromise({
    url: '/1/api/connections',
    data: args,
  });
}

export function updateConnection(
  connectionId: string,
  data: {
    name?: string;
    tags?: TagUpdateObject;
    dataCatalogFolderId?: string;
    isEnabled?: boolean;
    equipmentIds?: Array<string>;
  }
): Promise<{id: string}> {
  return net.patchJSONPromise({
    url: `/1/api/connections/${connectionId}`,
    data,
  });
}

export function updateConnectionOwnableInfo(
  connectionId: string,
  data: {
    collaborators: Array<DataCollaborator>;
    ownerInfo: OwnerInfo;
    owner: string;
  }
): Promise<{}> {
  return net.patchJSONPromise({
    url: `/1/api/connections/${connectionId}/collaborators`,
    data,
  });
}

interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<BulkArchiveResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/connections:bulk-archive',
    data: {ids, purpose},
  });
}

interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchive(ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/connections:bulk-unarchive',
    data: {ids},
  });
}

export function listAdapters(): Promise<{instrumentAdapters: Array<InstrumentAdapter>}> {
  return net.getJSONPromise(`/1/api/instruments/adapters`);
}

export function getAdapter(
  adapterId: string,
  gatewayType?: GatewayType
): Promise<{id: string; fields: Array<AdapterField>; transformDataTypes: {[id: string]: string}}> {
  const data = gatewayType ? {gatewayType} : {};
  return net.getJSONPromise(`/1/api/instruments/adapters/${adapterId}`, data);
}

export function getAdapterConfiguration(): Promise<Array<InstrumentAdapterConfiguration>> {
  return net.getJSONPromise(`/1/api/instruments/adapter-configuration`);
}

export function saveAdapterConfiguration(adapters: Array<InstrumentAdapterConfiguration>): Promise<{}> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/instruments/adapter-configuration`,
    data: {instrumentTypes: adapters},
  });
}

export function getInstrumentServiceDetails(connectionId: string): Promise<InstrumentServiceDetails> {
  return net.getJSONPromise(`/1/api/connections/${connectionId}/info`);
}
